import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../data/translations';
import { Menu, X, ArrowRight } from 'lucide-react';

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const t = translations;

  const isHomepage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const navLinks = [
    { path: '/', label: t.nav.home[language] },
    { path: '/about', label: t.nav.about[language] },
    { path: '/approach', label: t.nav.approach[language] },
    { path: '/activities', label: t.nav.activities[language] },
    { path: '/investments', label: t.nav.investments[language] },
    { path: '/team', label: t.nav.team[language] },
    { path: '/news', label: t.nav.news[language] },
    { path: '/contact', label: t.nav.contact[language] }
  ];

  const ctaLinks = [
    { label: language === 'fr' ? 'Investir avec nous' : 'Invest with us', link: '/contact?type=invest' },
    { label: language === 'fr' ? 'Travailler avec nous' : 'Join our team', link: '/contact?type=work' },
    { label: language === 'fr' ? 'Devenir partenaire' : 'Become a partner', link: '/contact?type=partner' }
  ];

  const getNavBackground = () => {
    if (isMenuOpen) return 'transparent';
    return scrolled ? 'rgba(10, 31, 53, 0.85)' : 'transparent';
  };

  return (
    <>
      {/* Main Navigation Bar */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          backgroundColor: getNavBackground(),
          backdropFilter: scrolled && !isMenuOpen ? 'blur(20px)' : 'none'
        }}
      >
        <div className="max-w-[1600px] mx-auto px-8 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link
              to="/"
              className="hover:opacity-80 transition-opacity z-[60] relative"
            >
              <img
                src="/logo.svg"
                alt="Ocean Peak Group"
                className="h-10 lg:h-12 w-auto"
              />
            </Link>

            {/* Right: Language + Menu Toggle */}
            <div className="flex items-center gap-6 z-[60] relative">
              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className="text-white text-xs font-semibold px-4 py-2 border border-white/30 hover:bg-white/10 transition-all duration-300"
                style={{
                  fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif",
                  letterSpacing: '0.1em'
                }}
              >
                {language === 'fr' ? 'EN' : 'FR'}
              </button>

              {/* Menu Toggle Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-white p-2 hover:opacity-80 transition-all duration-300 flex items-center gap-3"
                style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif" }}
              >
                <span className="text-xs font-semibold tracking-wider hidden sm:block">
                  {isMenuOpen ? 'CLOSE' : 'MENU'}
                </span>
                <div className="relative w-6 h-6 flex items-center justify-center">
                  <span
                    className={`absolute h-0.5 w-6 bg-white transition-all duration-300 ${isMenuOpen ? 'rotate-45' : '-translate-y-1.5'}`}
                  />
                  <span
                    className={`absolute h-0.5 w-6 bg-white transition-all duration-300 ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`}
                  />
                  <span
                    className={`absolute h-0.5 w-6 bg-white transition-all duration-300 ${isMenuOpen ? '-rotate-45' : 'translate-y-1.5'}`}
                  />
                </div>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Full-Screen Menu Overlay - Opens ABOVE Page */}
      <div
        className={`fixed inset-0 z-40 transition-all duration-700 ease-out ${
          isMenuOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
        style={{
          backgroundColor: '#0a1f35',
          transform: isMenuOpen ? 'translateY(0)' : 'translateY(-100%)'
        }}
      >
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute -top-1/2 -right-1/4 w-[800px] h-[800px] rounded-full opacity-5"
            style={{ background: 'radial-gradient(circle, #2a6cae 0%, transparent 70%)' }}
          />
          <div
            className="absolute -bottom-1/4 -left-1/4 w-[600px] h-[600px] rounded-full opacity-5"
            style={{ background: 'radial-gradient(circle, #4fb785 0%, transparent 70%)' }}
          />
        </div>

        {/* Menu Content */}
        <div className="relative h-full flex flex-col justify-center pt-32 lg:pt-24">
          <div className="max-w-[1600px] mx-auto px-8 lg:px-12 w-full">
            <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">

              {/* Left Column - Main Navigation */}
              <div className="space-y-2">
                {navLinks.map((link, idx) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`block group transition-all duration-300 ${
                      location.pathname === link.path ? 'opacity-100' : 'opacity-70 hover:opacity-100'
                    }`}
                    style={{
                      animation: isMenuOpen ? `fadeSlideIn 0.5s ease-out ${idx * 0.08}s both` : 'none'
                    }}
                  >
                    <div className="flex items-center gap-4 py-3">
                      <span
                        className="text-white text-3xl lg:text-5xl font-bold group-hover:translate-x-2 transition-transform duration-300"
                        style={{
                          fontFamily: "'Bank Gothic', 'Impact', sans-serif",
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em'
                        }}
                      >
                        {link.label}
                      </span>
                      <ArrowRight
                        className="text-white/0 group-hover:text-white/50 transition-all duration-300 group-hover:translate-x-2"
                        size={28}
                      />
                    </div>
                    {location.pathname === link.path && (
                      <div className="h-0.5 w-16 bg-[#2a6cae]" />
                    )}
                  </Link>
                ))}
              </div>

              {/* Right Column - CTAs & Info */}
              <div
                className="flex flex-col justify-center"
                style={{
                  animation: isMenuOpen ? 'fadeSlideIn 0.6s ease-out 0.4s both' : 'none'
                }}
              >
                <div className="mb-12">
                  <p
                    className="text-white/50 text-sm mb-4 uppercase tracking-wider"
                    style={{ fontFamily: "'Novecento Sans Wide', 'Arial', sans-serif" }}
                  >
                    {language === 'fr' ? 'Nous contacter' : 'Get in touch'}
                  </p>
                  <div className="space-y-3">
                    {ctaLinks.map((cta, idx) => (
                      <Link
                        key={idx}
                        to={cta.link}
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center gap-3 text-white text-lg hover:text-[#2a6cae] transition-colors group"
                        style={{ fontFamily: "'Eurostile', 'Arial', sans-serif" }}
                      >
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        {cta.label}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Tagline */}
                <div className="border-t border-white/10 pt-8">
                  <p
                    className="text-white/40 text-lg italic"
                    style={{ fontFamily: "'Eurostile', 'Arial', sans-serif" }}
                  >
                    "Into the depths — beyond the expected."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
};
